import type {
  AnyAction,
  Dispatch,
  EnhancedStore,
  ThunkDispatch,
  Tuple,
} from '@reduxjs/toolkit';
import { combineReducers, configureStore } from '@reduxjs/toolkit';
import type { TypedUseSelectorHook } from 'react-redux';
import { useDispatch, useSelector } from 'react-redux';
import logger from 'redux-logger';
import type { Storage } from 'redux-persist';
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  REHYDRATE,
  persistReducer,
  persistStore,
} from 'redux-persist';
import adminView from './reducers/adminView';
import appcache from './reducers/appcache';
import application from './reducers/application';
import configuration from './reducers/configuration';
import consultation from './reducers/consultation';
import conversation from './reducers/conversation';
import profile from './reducers/profile';
import session from './reducers/session';
import shopping from './reducers/shopping';
import tours from './reducers/tours';

const devMiddlewares = [logger];

const reducer = {
  session,
  application,
  configuration,
  conversation,
  consultation,
  appcache,
  tours,
  profile,
  adminView,
  shopping,
};

function createReducer(storage: Storage, key = 'athletes', isDev = false) {
  const combined = combineReducers(reducer);
  const persistedReducer = persistReducer(
    {
      key,
      version: 1,
      storage,
      whitelist: [
        'session',
        'application',
        'configuration',
        'conversation',
        'consultation',
        'appcache',
        'adminView',
        'shopping',
        // 'tours',
      ],
    },
    combined
  );

  const store = configureStore({
    devTools: isDev,
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
      [
        ...getDefaultMiddleware({
          immutableCheck: false,
          serializableCheck: {
            ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
          },
        }),
        ...(isDev ? devMiddlewares : []),
      ] as Tuple,
  });
  const persistor = persistStore(store);

  return { store, persistor };
}

export { createReducer };

type StoreType = EnhancedStore<
  ReturnType<ReturnType<typeof combineReducers<typeof reducer>>>
>;

export type RootState = ReturnType<StoreType['getState']>;
export type AppDispatch = ThunkDispatch<RootState, undefined, AnyAction> &
  Dispatch<AnyAction>;

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
